import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MeApi } from 'src/api';
import { PointsResponse, ProgressResponse } from 'src/api/me/types';

const MAX_POINTS_RETRIES = 5;
const BASE_INTERVAL = 200;

export const useLatestPointsAndProgress = (
  languagePairId: number | null | undefined,
  pointsBefore: PointsResponse | null | undefined,
  { disabled }: { disabled?: boolean } = {},
) => {
  const [isRefetchStopped, setIsRefetchStopped] = useState<boolean>(false);
  const [pointsRetries, setPointsRetries] = useState(0);

  const pointsEnabled = Boolean(
    languagePairId &&
    pointsBefore &&
    !disabled &&
    !isRefetchStopped &&
    pointsRetries < MAX_POINTS_RETRIES,
  );

  const { data: latestPoints, isError: isPointsError } = useQuery<PointsResponse | null>({
    queryKey: ['getLatestPoints', languagePairId],
    queryFn: () => {
      /* istanbul ignore if: should be defined */
      if (!languagePairId) return null;
      setPointsRetries(r => r + 1);
      return MeApi.getPoints(languagePairId);
    },
    enabled: pointsEnabled,
    refetchInterval: BASE_INTERVAL,
  });

  const pointsAfter =
    pointsBefore && latestPoints && latestPoints.lastUpdated !== pointsBefore.lastUpdated
      ? latestPoints
      : null;

  useEffect(() => {
    if (!isRefetchStopped && pointsAfter) {
      setIsRefetchStopped(true);
    }
  }, [isRefetchStopped, pointsAfter]);

  const { data: progressAfter, isLoading: isLoadingProgress } = useQuery<ProgressResponse | null>({
    queryKey: ['getProgressAfter', languagePairId],
    queryFn: () => {
      /* istanbul ignore if: should be defined */
      if (!languagePairId) return null;
      return MeApi.getProgress(languagePairId);
    },
    enabled: !!pointsAfter,
  });

  return {
    pointsAfter,
    progressAfter,
    isLoadingPointsAndProgress:
      (pointsEnabled && !pointsAfter && !isPointsError) || isLoadingProgress,
  };
};
